import React from 'react';
import { StyleSheet, View } from 'react-native';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

export enum ListItemLineFormat {
  LABEL = 'LABEL',
  CAPTION = 'CAPTION',
}

export enum ListItemTextStackAlignment {
  LEADING = 'LEADING',
  TRAILING = 'TRAILING',
}

export interface ListItemTextStackProps {
  primaryCopy: string;
  primaryCopyColor?: Maybe<string>;
  secondaryCopy?: Maybe<string>;
  lineFormat?: Maybe<ListItemLineFormat>;
  isDisabled?: Maybe<boolean>;
  textAlignment?: Maybe<ListItemTextStackAlignment>;
  primaryNumberOfLines?: number;
  secondaryNumberOfLines?: number;
  boldPrimaryCopy?: Maybe<boolean>;
}

export const isListItemTextStackProps = (
  value: any,
): value is ListItemTextStackProps => {
  if ('primaryCopy' in value) {
    return true;
  }
  return false;
};

const getSecondaryNumberOfLines = (
  prop: Maybe<number>,
  lineFormat: Maybe<ListItemLineFormat>,
) => {
  if (prop) {
    return prop;
  }

  if (lineFormat === ListItemLineFormat.CAPTION) {
    return 3;
  }

  return 1;
};

const ListItemTextStack = ({
  lineFormat = ListItemLineFormat.CAPTION,
  primaryCopy,
  primaryCopyColor,
  secondaryCopy,
  isDisabled = false,
  textAlignment = ListItemTextStackAlignment.LEADING,
  primaryNumberOfLines = 2,
  secondaryNumberOfLines,
  boldPrimaryCopy,
}: ListItemTextStackProps) => {
  const primaryStyle = isDisabled
    ? styles.textPrimaryDisabled
    : { color: primaryCopyColor || theme.color.text.primary };

  const secondaryStyle = isDisabled
    ? styles.textSecondaryDisabled
    : styles.textSecondary;

  const alignmentStyle =
    textAlignment === ListItemTextStackAlignment.LEADING
      ? styles.textAlignLeading
      : styles.textAlignTrailing;

  const parsedSecondaryNumberOfLines = getSecondaryNumberOfLines(
    secondaryNumberOfLines,
    lineFormat,
  );

  const primaryText =
    primaryCopy?.length === 0 ? null : (
      <BodyBaseText
        style={[alignmentStyle, primaryStyle]}
        numberOfLines={primaryNumberOfLines}
        testID="listitem-primary-label"
        accessible
      >
        {primaryCopy}
      </BodyBaseText>
    );

  const primaryTextBold =
    primaryCopy?.length === 0 ? null : (
      <BodyBaseBoldText
        style={[alignmentStyle, primaryStyle]}
        numberOfLines={primaryNumberOfLines}
        testID="listitem-primary-label"
        accessible
      >
        {primaryCopy}
      </BodyBaseBoldText>
    );

  const secondaryText =
    !secondaryCopy || secondaryCopy?.length === 0 ? null : (
      <BodyXSText
        style={[alignmentStyle, secondaryStyle]}
        numberOfLines={parsedSecondaryNumberOfLines}
        testID="listitem-secondary-label"
        accessible
      >
        {secondaryCopy}
      </BodyXSText>
    );

  if (lineFormat === ListItemLineFormat.LABEL) {
    return (
      <View style={styles.container}>
        {secondaryText}
        {primaryText}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {boldPrimaryCopy ? primaryTextBold : primaryText}
      {secondaryText}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexShrink: 1,
    minWidth: 50,
  },
  textAlignLeading: {
    flexWrap: 'wrap',
    textAlign: 'left',
  },
  textAlignTrailing: {
    flexWrap: 'wrap',
    textAlign: 'right',
  },
  textPrimaryDisabled: {
    color: theme.color.text.muted,
  },
  textSecondary: {
    color: theme.color.text.secondary,
  },
  textSecondaryDisabled: {
    color: theme.color.text.muted,
  },
});

export default ListItemTextStack;
